import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionType } from '../../bx-select.types';
import type { CalculationPath, SourceRelationOption, SourceRelationValue } from '../types';
import {
  locationOptions,
  buildOption,
  hideShowPlacement,
  hideShowAttribute,
  hideShowOutput,
  hideShowSubLocation,
  hideShowCustomModuleTab,
  hideShowCustomModuleSection,
  hideShowCustomModuleSubSection,
  hideShowCustomModuleControl,
  setPlacementForLocation,
  setSubLocationArr,
  setOptionsForAttribute,
  setOptionsForOutput,
  setOptionsForBomOutput,
  isPathComplete,
} from './calculation-config.utils';
import { prouctCustmSectionAttributeList } from '../../../helper';
import { ClearIcon, DeleteIcon } from '../../../../bombiix-icon';
import { NewSelectMultiSelect } from '../../../PIM/common/NewSelectMultiSelect';
import { NewInput } from '../../../PIM/common/NewInput';

/**
 * The master-design select reports `Option | readonly Option[] | null`. Every call site
 * here is a single-select, so narrow to the one option's value.
 * @param option value handed back by NewSelectMultiSelect
 * @returns the selected option's value, or '' when cleared
 */
const selectedValue = (option: OptionType | readonly OptionType[] | null | undefined): string =>
  Array.isArray(option) ? '' : ((option as OptionType | null)?.value ?? '');


type FormulaDisplayProps = {
  formulaInput: string;
  paths?: CalculationPath[];
  disabled?: boolean;
};

export const FormulaDisplay: React.FC<FormulaDisplayProps> = ({ formulaInput, paths, disabled = false }) => {
  const formula = formulaInput.trim();
  if (!formula) {
    return <span className="cm-calculation-formula-text-placeholder"> </span>;
  }

  const pathNames =
    paths
      ?.filter((path) => path?.pathName && path.pathName.trim().length > 0)
      .map((path) => path.pathName?.trim() ?? '')
      .filter((name) => name.length > 0)
      .sort((a, b) => b.length - a.length) || [];

  if (pathNames.length === 0) {
    return <span className="cm-calculation-formula-text">{formula}</span>;
  }

  const tokens: Array<{ type: 'path' | 'text'; value: string; startIndex: number }> = [];
  const usedIndices = new Set<number>();

  pathNames.forEach((pathName) => {
    let searchIndex = 0;
    while (true) {
      const pathIndex = formula.indexOf(pathName, searchIndex);
      if (pathIndex === -1) break;

      if (!usedIndices.has(pathIndex)) {
        tokens.push({
          type: 'path',
          value: pathName,
          startIndex: pathIndex,
        });
        for (let i = pathIndex; i < pathIndex + pathName.length; i++) {
          usedIndices.add(i);
        }
      }
      searchIndex = pathIndex + 1;
    }
  });

  tokens.sort((a, b) => a.startIndex - b.startIndex);

  const finalTokens: Array<{ type: 'path' | 'text'; value: string }> = [];
  let lastIndex = 0;

  tokens.forEach((token) => {
    if (token.startIndex > lastIndex) {
      const textPart = formula.substring(lastIndex, token.startIndex);
      if (textPart) {
        finalTokens.push({
          type: 'text',
          value: textPart,
        });
      }
    }
    finalTokens.push({
      type: 'path',
      value: token.value,
    });
    lastIndex = token.startIndex + token.value.length;
  });

  if (lastIndex < formula.length) {
    const textPart = formula.substring(lastIndex);
    if (textPart) {
      finalTokens.push({
        type: 'text',
        value: textPart,
      });
    }
  }

  if (finalTokens.length === 0) {
    return <span className="cm-calculation-formula-text">{formula}</span>;
  }

  return (
    <div className="cm-calculation-formula-text d-flex flex-wrap align-items-center gap-1">
      {finalTokens.map((token, idx) => {
        if (token.type === 'path') {
          return (
            <button
              key={`${token.value}-${idx}`}
              type="button"
              className="cm-calculation-path-badge-display cm-calculation-path-badge-in-formula"
              disabled={disabled}
              title={token.value}
            >
              {token.value}
            </button>
          );
        }
        return (
          <span key={`text-${idx}`} className="cm-calculation-formula-text-part">
            {token.value}
          </span>
        );
      })}
    </div>
  );
};

type FormulaConditions = {
  percentage: boolean;
  root: boolean;
  disableNumbers: boolean;
  disableSpecialCharacter: boolean;
  squareAtLast: boolean;
  piAtLast: boolean;
  piSquareAtLast: boolean;
  numberSquareAtLast: boolean;
  closingBreak: boolean;
  hasPaths: boolean;
};

type FormulaButtonsProps = {
  setFormulaInput: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
  formulaConditions: FormulaConditions;
  hasStringTypePath: boolean;
  hasPathInFormula: boolean;
  counterState: boolean;
  onClearFormula: () => void;
};

export const FormulaButtons: React.FC<FormulaButtonsProps> = ({
  setFormulaInput,
  disabled = false,
  formulaConditions,
  hasStringTypePath,
  hasPathInFormula,
  counterState,
  onClearFormula,
}) => {
  const handleSquareClick = () => {
    setFormulaInput((prev) => {
      const trimmed = prev.trim();
      if (trimmed.endsWith('π')) {
        return trimmed.slice(0, -1) + 'π²';
      }
      let i = trimmed.length - 1;
      while (i >= 0 && trimmed[i] === ' ') {
        i--;
      }
      if (i < 0) {
        return prev + 'X²';
      }
      let end = i + 1;
      let start = i;
      while (start >= 0) {
        const char = trimmed[start];
        if (char === undefined || char === ' ') {
          break;
        }
        const operators = ['×', '÷', '&', '(', ')', '%', '√', 'π'];
        if (operators.includes(char)) {
          break;
        }
        if (char === '²' && start > 0 && (trimmed[start - 1] === 'X' || trimmed[start - 1] === 'π')) {
          start--;
          break;
        }
        if (char === '+' || char === '-') {
          if (start > 0) {
            const prevChar = trimmed[start - 1];
            if (prevChar && /[0-9.]/.test(prevChar)) {
              break;
            }
          }
        }
        if (!/[\d.+-]/.test(char)) {
          break;
        }
        start--;
      }
      start++;
      const candidate = trimmed.substring(start, end).trim();
      if (candidate && /^[+-]?\d+\.?\d*$/.test(candidate)) {
        return trimmed.slice(0, start) + candidate + '²' + trimmed.slice(end);
      }
      return prev + 'X²';
    });
  };

  return (
    <div className="cm-calculation-buttons-grid">
      <div className="cm-calculation-button-row">
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '√')}
          disabled={
            disabled || formulaConditions.percentage || hasStringTypePath || formulaConditions.disableNumbers || formulaConditions.disableSpecialCharacter
          }
        >
          <span className="cm-calculation-glyph">{'√'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={handleSquareClick}
          disabled={disabled || formulaConditions.percentage || hasStringTypePath || counterState || formulaConditions.root || formulaConditions.squareAtLast}
        >
          <span className="cm-calculation-glyph">{'x²'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + 'π')}
          disabled={
            disabled || formulaConditions.percentage || hasStringTypePath || formulaConditions.disableSpecialCharacter || formulaConditions.disableNumbers
          }
        >
          <span className="cm-calculation-glyph">{'π'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => {
            setFormulaInput((prev) => {
              const trimmed = prev.trim();
              if (trimmed.endsWith('&')) {
                return prev;
              }
              return prev + '&';
            });
          }}
          disabled={
            disabled || formulaConditions.percentage || counterState || !hasStringTypePath || formulaConditions.root || formulaConditions.disableNumbers
          }
        >
          <span className="cm-calculation-glyph">{'&'}</span>
        </button>
        <button type="button" className="cm-calculation-btn cm-calculation-btn-clear" onClick={onClearFormula} disabled={disabled}>
          <ClearIcon size={24} />
        </button>
      </div>
      <div className="cm-calculation-button-row">
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '7')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'7'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '8')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'8'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '9')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'9'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '(')}
          disabled={
            disabled ||
            (hasStringTypePath && !hasPathInFormula) ||
            formulaConditions.percentage ||
            formulaConditions.disableSpecialCharacter ||
            formulaConditions.squareAtLast ||
            formulaConditions.piAtLast ||
            formulaConditions.piSquareAtLast
          }
        >
          <span className="cm-calculation-glyph">{'('}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + ')')}
          disabled={disabled || (formulaConditions.closingBreak && !hasPathInFormula)}
        >
          <span className="cm-calculation-glyph">{')'}</span>
        </button>
      </div>
      <div className="cm-calculation-button-row">
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '4')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'4'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '5')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'5'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '6')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'6'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '%')}
          disabled={disabled || formulaConditions.percentage || hasStringTypePath || counterState || formulaConditions.root || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'%'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '.')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'.'}</span>
        </button>
      </div>
      <div className="cm-calculation-button-row">
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '1')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'1'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '2')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'2'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '3')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'3'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '×')}
          disabled={disabled || hasStringTypePath || counterState || formulaConditions.root}
        >
          <span className="cm-calculation-glyph">{'×'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '÷')}
          disabled={disabled || hasStringTypePath || counterState || formulaConditions.root}
        >
          <span className="cm-calculation-glyph">{'÷'}</span>
        </button>
      </div>
      <div className="cm-calculation-button-row">
        <button
          type="button"
          className="cm-calculation-btn cm-calculation-btn-zero"
          onClick={() => setFormulaInput((prev) => prev + '0')}
          disabled={disabled || formulaConditions.percentage || formulaConditions.disableNumbers}
        >
          <span className="cm-calculation-glyph">{'0'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '+')}
          disabled={disabled || hasStringTypePath || counterState || formulaConditions.root}
        >
          <span className="cm-calculation-glyph">{'+'}</span>
        </button>
        <button
          type="button"
          className="cm-calculation-btn"
          onClick={() => setFormulaInput((prev) => prev + '-')}
          disabled={disabled || hasStringTypePath || counterState || formulaConditions.root}
        >
          <span className="cm-calculation-glyph">{'−'}</span>
        </button>
      </div>
    </div>
  );
};

type PathConfigProps = {
  path: CalculationPath;
  index: number;
  disabled?: boolean;
  editingPathIndex: number | null;
  setEditingPathIndex: React.Dispatch<React.SetStateAction<number | null>>;
  updatePath: (index: number, updates: Partial<CalculationPath>) => void;
  updatePathRelation: (pathIndex: number, key: keyof SourceRelationValue, optionName: string) => void;
  handleCreatePath: (pathIndex: number) => void;
  requestDeletePath: (pathIndex: number) => void;
  dynamicBomSelection: SourceRelationOption[];
  sampleTypeFromStore: SourceRelationOption[];
  locationOptions?: SourceRelationOption[];
  placement_master?: Array<{ _id: string; name: string; bomType?: string[] }>;
  manualCustomColumns?: SourceRelationOption[];
  customAttributeDetail?: Array<{ _id: string; keyName: string; isActive?: boolean }>;
  sampleTrackingDates?: Array<{ _id: string; name: string }>;
  subBrandAccess?: boolean;
  cmModuleList?: SourceRelationOption[];
  cmTabList?: SourceRelationOption[];
  cmSectionList?: SourceRelationOption[];
  cmSubSectionList?: SourceRelationOption[];
  cmControlList?: SourceRelationOption[];
};

export const PathConfig: React.FC<PathConfigProps> = ({
  path,
  index,
  disabled = false,
  editingPathIndex,
  setEditingPathIndex,
  updatePath,
  updatePathRelation,
  handleCreatePath,
  requestDeletePath,
  dynamicBomSelection,
  sampleTypeFromStore,
  locationOptions: locationOptionsProp,
  placement_master,
  manualCustomColumns,
  customAttributeDetail,
  sampleTrackingDates,
  subBrandAccess,
  cmModuleList = [],
  cmTabList = [],
  cmSectionList = [],
  cmSubSectionList = [],
  cmControlList = [],
}) => {
  const { t } = useTranslation('custom-module-system-canvas');

  const toSelectValue = (opt: SourceRelationOption | null | undefined): OptionType | null => (opt ? { label: opt.label, value: opt.name } : null);
  const toOptions = (opts: SourceRelationOption[]): OptionType[] => opts.map((option) => ({ label: option.label, value: option.name }));
  const effectiveLocationOptions = locationOptionsProp ?? locationOptions;

  const pathIsComplete = isPathComplete(path);
  const hasBreadcrumb = !!path.breadcrumb && path.breadcrumb.trim().length > 0;
  const isEditing = editingPathIndex === index;
  const showConfig = !pathIsComplete || isEditing || !hasBreadcrumb;

  return (
    <div className="mb-3 cm-calculation-path-card">
      <div className="mb-2 d-flex align-items-center justify-content-between">
        <label className="mb-0">
          {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.PATH_NAME')} <span className="red">*</span>
        </label>
        <button
          type="button"
          className="btn p-0 border-none bg-transparent"
          onClick={() => requestDeletePath(index)}
          disabled={disabled}
          aria-label="Delete Path"
          title={t('COMMON.DELETE') || 'Delete'}
        >
          <DeleteIcon size={24} className="cursor-pointer" style={{ color: '#f65158' }} />
        </button>
      </div>
      <div className="mb-2">
        <NewInput
          type="text"
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.PATH_NAME_PLACEHOLDER')}
          value={path.pathName ?? ''}
          onChange={(e) => updatePath(index, { pathName: e.target.value })}
          disabled={disabled}
        />
      </div>

      {pathIsComplete && hasBreadcrumb && !isEditing && (
        <div className="mb-2">
          <label className="mb-1 d-block">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.BREADCRUMB')}</label>
          <div onClick={() => !disabled && setEditingPathIndex(index)} style={{ cursor: disabled ? 'default' : 'pointer' }}>
            <NewSelectMultiSelect
              menuPortalTarget={document.body}
              menuPosition="fixed"
              value={toSelectValue(path.breadcrumb ? { label: path.breadcrumb, name: path.breadcrumb } : null)}
              options={[]}
              onChange={() => {}}
              placeholder={path.breadcrumb ?? ''}
              isDisabled={true}
              containerClassName="w-100"
            />
          </div>
        </div>
      )}

      {showConfig && (
        <>
          <div className="mb-2">
            <label className="mb-1 d-block">
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.LOCATION')} <span className="red">*</span>
            </label>
            <NewSelectMultiSelect
              menuPortalTarget={document.body}
              menuPosition="fixed"
              value={toSelectValue(path.relation?.location ? { label: path.relation.location.label ?? '', name: path.relation.location.name ?? '' } : null)}
              options={toOptions(effectiveLocationOptions)}
              onChange={(option) => updatePathRelation(index, 'location', selectedValue(option))}
              placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SELECT_LOCATION')}
              isDisabled={disabled}
              containerClassName="w-100"
            />
          </div>

          {['Bill_of_Materials', 'Samples'].includes(path.relation?.location?.name || '') && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_TYPE')} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.locationType)}
                options={toOptions(path.relation?.location?.name === 'Samples' ? (sampleTypeFromStore.length ? sampleTypeFromStore : []) : dynamicBomSelection)}
                onChange={(option) => updatePathRelation(index, 'locationType', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {hideShowPlacement(path.relation?.location?.name, path.relation?.locationType?.name) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.PLACEMENT')} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.locationPlacement)}
                options={toOptions(setPlacementForLocation(path.relation?.location?.name, path.relation?.locationType?.name, placement_master))}
                onChange={(option) => updatePathRelation(index, 'locationPlacement', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SELECT_PLACEMENT')}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowAttribute(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_LIBRARY.SOURCE_TYPE.ATTRIBUTE')} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.attribute)}
                options={toOptions(
                  (path.relation.location?._id === 12
                    ? (prouctCustmSectionAttributeList as Array<{ label: string; name: string }>).map((attributeItem) =>
                        buildOption(attributeItem.label, attributeItem.name),
                      )
                    : setOptionsForAttribute(path.relation)) || [],
                )}
                onChange={(option) => updatePathRelation(index, 'attribute', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowOutput(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_LIBRARY.SOURCE_TYPE.OUTPUT')} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.output)}
                options={toOptions(
                  path.relation.location?.name === 'Bill_of_Materials' ? setOptionsForBomOutput(path.relation) : setOptionsForOutput(path.relation),
                )}
                onChange={(option) => updatePathRelation(index, 'output', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT')}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowSubLocation(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SUB_LOCATION')} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.subLocation)}
                options={toOptions(
                  setSubLocationArr(
                    path.relation?.location?._id,
                    path.relation?.location?.name,
                    manualCustomColumns,
                    customAttributeDetail,
                    sampleTrackingDates,
                    subBrandAccess,
                  ),
                )}
                onChange={(option) => updatePathRelation(index, 'subLocation', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SELECT_SUB_LOCATION')}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation?.location?.name === 'Custom_Modules' && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.MODULE') || 'Module'} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.customModule)}
                options={toOptions(cmModuleList)}
                onChange={(option) => updatePathRelation(index, 'customModule', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT') || 'Select'}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowCustomModuleTab(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.TAB') || 'Tab'} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.customModuleTab)}
                options={toOptions(cmTabList)}
                onChange={(option) => updatePathRelation(index, 'customModuleTab', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT') || 'Select'}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowCustomModuleSection(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SECTION') || 'Section'} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.customModuleSection)}
                options={toOptions(cmSectionList)}
                onChange={(option) => updatePathRelation(index, 'customModuleSection', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT') || 'Select'}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowCustomModuleSubSection(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.SUBSECTION') || 'Subsection'} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.customModuleSubSection)}
                options={toOptions(cmSubSectionList)}
                onChange={(option) => updatePathRelation(index, 'customModuleSubSection', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT') || 'Select'}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}

          {path.relation && hideShowCustomModuleControl(path.relation) && (
            <div className="mb-2">
              <label className="mb-1 d-block">
                {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.FIELD') || 'Field'} <span className="red">*</span>
              </label>
              <NewSelectMultiSelect
                menuPortalTarget={document.body}
                menuPosition="fixed"
                value={toSelectValue(path.relation?.customModuleControl)}
                options={toOptions(cmControlList)}
                onChange={(option) => updatePathRelation(index, 'customModuleControl', selectedValue(option))}
                placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT') || 'Select'}
                isDisabled={disabled}
                containerClassName="w-100"
              />
            </div>
          )}
        </>
      )}

      {!(pathIsComplete && hasBreadcrumb && !isEditing) && (
        <div className="d-flex justify-content-center mt-3">
          <button
            type="button"
            className="bombiix-btn btn-blue"
            onClick={() => handleCreatePath(index)}
            disabled={disabled || !path.pathName || !path.relation?.location || !isPathComplete(path)}
          >
            {hasBreadcrumb
              ? t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.UPDATE') || t('COMMON.UPDATE') || 'Update'
              : t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CALCULATION.CREATE')}
          </button>
        </div>
      )}
    </div>
  );
};
